// src/services/enhancedPhotoUrlService.ts
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { getPhotoAccess } from '../graphql/queries';

const client = generateClient();

// Queue for tracking pending requests
const pendingFullRequests: { [key: string]: Promise<string> } = {};
const pendingThumbnailRequests: { [key: string]: Promise<string> } = {};

// Background loading queue with priority
let backgroundQueue: { photoId: string; thumbnailOnly: boolean }[] = [];
let isProcessingQueue = false;
const BATCH_SIZE = 3; // Number of photos to load in parallel
const LOAD_DELAY = 50; // Millisecond delay between batches

// Keep a reference to auth credentials for reuse
let cachedSession: any = null;
let cachedCredentials: any = null;

// Cache for successful URL retrievals
const cachedPhotoUrls: { [key: string]: { url: string, expires: number } } = {};
const cachedThumbnailUrls: { [key: string]: { url: string, expires: number } } = {};
const CACHE_EXPIRY = 1000 * 60 * 60; // 1 hour

const urlCache = {
    thumbnails: new Map<string, { url: string, expires: number }>(),
    fullImages: new Map<string, { url: string, expires: number }>()
};

const URL_CACHE_DURATION = 3600 * 1000; // 1 hour in milliseconds

// Add a function to check if URL is cached
const getUrlFromCache = (photoId: string, thumbnailOnly: boolean): string | null => {
    const cache = thumbnailOnly ? urlCache.thumbnails : urlCache.fullImages;
    const entry = cache.get(photoId);

    if (entry && entry.expires > Date.now()) {
        return entry.url;
    }

    return null;
};

// Add a function to save URL to cache
const saveUrlToCache = (photoId: string, url: string, thumbnailOnly: boolean) => {
    const cache = thumbnailOnly ? urlCache.thumbnails : urlCache.fullImages;
    cache.set(photoId, {
        url,
        expires: Date.now() + URL_CACHE_DURATION
    });
};

/**
 * Get a cached session or fetch a new one if needed
 */
const getAuthSession = async (): Promise<any> => {
    if (!cachedSession) {
        const session = await fetchAuthSession();
        cachedSession = session;
        cachedCredentials = session.credentials;
    }
    return {
        session: cachedSession,
        credentials: cachedCredentials
    };
};


/**
 * Load a single photo URL (on demand)
 * @param photoId Photo ID to load
 * @param thumbnailOnly Whether to load only the thumbnail
 * @param priority Whether this is a high-priority request
 */
export const loadPhotoUrl = async (
    photoId: string,
    thumbnailOnly: boolean = false,
    priority: boolean = false
): Promise<string> => {

    const cachedUrl = getUrlFromCache(photoId, thumbnailOnly);
    if (cachedUrl) {
        return cachedUrl;
    }
    const pendingRequests = thumbnailOnly ? pendingThumbnailRequests : pendingFullRequests;


    // Return existing promise if this photo is already being loaded
    if (photoId in pendingRequests) {
        return pendingRequests[photoId];
    }

    // Create a new request promise
    const requestPromise = (async () => {
        try {
            // Get authentication session
            const { credentials } = await getAuthSession();

            // Get photo access information
            const response = await client.graphql({
                query: getPhotoAccess,
                variables: {
                    photoId,
                    thumbnailOnly
                },
                authMode: "userPool"
            });

            const photoAccess = response.data.getPhotoAccess;
            if (!photoAccess) {
                throw new Error(`No photo access information returned for photo ${photoId}`);
            }

            // Create an S3 client
            const s3Client = new S3Client({
                region: photoAccess.region,
                credentials: credentials
            });

            // Create a GetObjectCommand
            const command = new GetObjectCommand({
                Bucket: photoAccess.bucket,
                Key: photoAccess.key,
                ResponseCacheControl: 'max-age=3600, immutable'  // Cache for 1 hour
            });

            // Generate a presigned URL
            const presignedUrl = await getSignedUrl(s3Client, command, {
                expiresIn: 3600 // 1 hour
            });

            const url = presignedUrl.toString();
            saveUrlToCache(photoId, url, thumbnailOnly);

            return url;
        } catch (error) {
            console.error(`Error loading URL for photo ${photoId}:`, error);
            throw error;
        } finally {
            // Remove from pending requests
            delete pendingRequests[photoId];
        }
    })();

    // Store the promise in pending requests
    pendingRequests[photoId] = requestPromise;

    return requestPromise;
};

/**
 * Specifically load a thumbnail URL
 */
export const loadPhotoThumbnailUrl = async (photoId: string, priority: boolean = false): Promise<string> => {
    return loadPhotoUrl(photoId, true, priority);
};

/**
 * Process the background loading queue
 */
const processBackgroundQueue = async () => {
    if (isProcessingQueue || backgroundQueue.length === 0) return;

    isProcessingQueue = true;

    try {
        while (backgroundQueue.length > 0) {
            // Process a batch of photos
            const batch = backgroundQueue.splice(0, BATCH_SIZE);

            // Load this batch in parallel
            await Promise.all(batch.map(({ photoId, thumbnailOnly }) => {
                // Skip if already loaded or loading
                const pendingRequests = thumbnailOnly ? pendingThumbnailRequests : pendingFullRequests;

                if (photoId in pendingRequests) return null;

                // Load with low priority
                return loadPhotoUrl(photoId, thumbnailOnly, false).catch(err => {
                    // Silently handle errors for background loading
                    console.warn(`Background loading failed for photo ${photoId}:`, err);
                });
            }));

            // Add a small delay between batches to avoid overwhelming the system
            if (backgroundQueue.length > 0) {
                await new Promise(resolve => setTimeout(resolve, LOAD_DELAY));
            }
        }
    } finally {
        isProcessingQueue = false;
    }
};

/**
 * Add photos to the background loading queue
 */
export const queueBackgroundLoading = (
    photoIds: string[],
    thumbnailOnly: boolean = false
) => {
    // Filter out IDs that are already loaded or in the queue
    const pendingRequests = thumbnailOnly ? pendingThumbnailRequests : pendingFullRequests;

    const newItems = photoIds.filter(id => {
        if (id in pendingRequests) return false;

        // Also check if already in queue
        return !backgroundQueue.some(item =>
            item.photoId === id && item.thumbnailOnly === thumbnailOnly
        );
    }).map(id => ({ photoId: id, thumbnailOnly }));

    if (newItems.length === 0) return;

    // Add to the queue
    backgroundQueue.push(...newItems);

    // Start processing if not already running
    if (!isProcessingQueue) {
        setTimeout(processBackgroundQueue, 0);
    }
};

/**
 * Preload both thumbnail and full image for a photo
 */
export const preloadPhoto = async (photoId: string): Promise<void> => {
    // First load thumbnail if not already cached
    try {
        await loadPhotoThumbnailUrl(photoId, true);
    } catch (error) {
        console.warn(`Failed to preload thumbnail for ${photoId}:`, error);
    }
    // Then start loading full image in background if needed
    try {
        loadPhotoUrl(photoId, false, false).catch(() => { });  // Start loading but don't wait
    } catch (error) {
        // Ignore errors in background loading
    }

};

/**
 * Load visible photos and queue nearby ones for both thumbnails and full images
 */
// In enhancedPhotoUrlService.ts
export const loadVisibleAndNearbyPhotos = async (
    visibleIds: string[],
    allPhotoIds: string[],
    preloadCount: number = 10,
    thumbnailsOnly: boolean = true
) => {
    // Skip if no photos are visible
    if (visibleIds.length === 0) return {};

    const loadedUrls: { [key: string]: string } = {};

    // IMPORTANT FIX: Check which photos actually need loading
    const visibleIdsToLoad = visibleIds.filter(id => {
        // Skip if this URL is already in the corresponding cache
        return thumbnailsOnly ?
            !pendingThumbnailRequests[id] && !cachedThumbnailUrls[id] :
            !pendingFullRequests[id] && !cachedPhotoUrls[id];
    });

    // Only load photos that are not already loaded or loading
    if (visibleIdsToLoad.length > 0) {
        await Promise.all(visibleIdsToLoad.map(async (id) => {
            try {
                // Load with high priority
                const url = await loadPhotoUrl(id, thumbnailsOnly, true);
                loadedUrls[id] = url;
            } catch (error) {
                console.error(`Error loading visible photo ${id}:`, error);
            }
        }));
    }

    // Similar filtering for preloading - don't queue already loaded/loading photos
    const preloadIds: string[] = [];
    // [rest of the preloading logic]

    // IMPORTANT: Only queue photos that haven't been loaded already
    const filteredPreloadIds = preloadIds.filter(id => {
        return thumbnailsOnly ?
            !pendingThumbnailRequests[id] && !cachedThumbnailUrls[id] :
            !pendingFullRequests[id] && !cachedPhotoUrls[id];
    });

    // Queue background loading for nearby photos that need loading
    if (filteredPreloadIds.length > 0) {
        queueBackgroundLoading(filteredPreloadIds, thumbnailsOnly);
    }

    return loadedUrls;
};
